import AppPaths from '@globals/paths'
import { AppPermission } from '@shared/permissions'
import { T } from '@transifex/react'
import { ChecklistSection } from '../../types/dashboard'

export enum LaunchSectionsEnum {
  REQUIRED_FOR_LAUNCH = 'required-for-launch',
  LAUNCH = 'launch',
}

export const checklistSections: ChecklistSection[] = [
  {
    key: LaunchSectionsEnum.REQUIRED_FOR_LAUNCH,
    title: <T _str="Required for launch" />,
    items: [
      {
        key: 'import-users',
        title: <T _str="Import users" />,
        description: (
          <T _str="For always up-to-date data, HRIS integration is recommended, but the best import method depends on how your organization manages employee information." />
        ),
        isCompleted: false,
        helpText: (
          <T _str="You can combine methods. For example, use HRIS for most users and manually add individuals for pilots or special access." />
        ),
        subItems: [
          {
            key: 'hris-integration',
            title: <T _str="HRIS integration" />,
            description: <T _str="Automatically sync users from your HRIS into Kudos." />,
            actionUrl: AppPaths.tenant.admin.hrisIntegrations('records'),
            visible: hasAppPermission =>
              hasAppPermission('hrIntegrationApp', AppPermission.HrIntegrationAppBrowse),
          },
          {
            key: 'add-users-manually',
            title: <T _str="Manually add users individually or in bulk" />,
            description: (
              <T _str="Add users individually as needed, or use a CSV file for bulk uploads." />
            ),
            actionUrl: AppPaths.tenant.admin.members('users'),
            visible: hasAppPermission =>
              hasAppPermission('identityApp', AppPermission.IdentityAppImportRecords),
          },
          {
            key: 'ftp-integration',
            title: <T _str="FTP integration" />,
            description: (
              <T _str="Automatically upload and update user data in Kudos via secure file transfers. " />
            ),
            actionUrl: AppPaths.tenant.admin.configureFtp(),
            visible: hasAppPermission =>
              hasAppPermission(
                'hrIntegrationApp',
                AppPermission.HrIntegrationAppCreateFtpConnection,
              ),
          },
        ],
      },
      {
        key: 'create-teams',
        title: <T _str="Create teams" />,
        description: (
          <>
            <T _str="Teams let you segment users based on your organization’s structure so you can:" />
            <ul>
              <li>
                <span>
                  <T _str="Control access to specific features and permissions" />
                </span>
              </li>
              <li>
                <span>
                  <T _str="Segment users for tailored point allocations" />
                </span>
              </li>
              <li>
                <span>
                  <T _str="Target the right audience for campaigns" />
                </span>
              </li>
              <li>
                <span>
                  <T _str="Filter and break down data in reports and analytics" />
                </span>
              </li>
            </ul>
          </>
        ),
        isCompleted: false,
        helpText: (
          <T _str="You don't need to use every team type, just combine the ones that fit your organization's needs." />
        ),
        subItems: [
          {
            key: 'quick-teams',
            title: <T _str="Quick teams" />,
            description: (
              <T _str="Automatically generate teams based on attributes like department or location, making it easy to segment users without manual setup. Quick teams stay in sync with your data, so membership updates in real time as information changes." />
            ),
            actionUrl: AppPaths.tenant.admin.members('quick-teams'),
            visible: hasAppPermission =>
              hasAppPermission('identityApp', AppPermission.IdentityAppBrowse),
          },
          {
            key: 'manual-automated-teams',
            title: <T _str="Manual and automated teams" />,
            description: (
              <T _str="Create manual teams for full control, or build automated teams using rules that update membership automatically." />
            ),
            actionUrl: AppPaths.tenant.admin.members('teams'),
            visible: hasAppPermission =>
              hasAppPermission('identityApp', AppPermission.IdentityAppBrowse),
          },
        ],
      },
      {
        key: 'configure-security-access',
        title: <T _str="Configure security and access" />,
        isCompleted: false,
        subItems: [
          {
            key: 'sso-mfa-password-expiry',
            title: <T _str="SSO, MFA, and password expiry" />,
            description: (
              <T _str="Manage authentication and security settings like SSO, multi-factor authentication, and password expiry to keep your account safe and compliant." />
            ),
            actionUrl: AppPaths.tenant.admin.security(),
            visible: hasAppPermission =>
              hasAppPermission('identityApp', AppPermission.IdentityAppBrowse),
          },
        ],
      },
      {
        key: 'add-branding',
        title: <T _str="Add branding" />,
        isCompleted: false,
        subItems: [
          {
            key: 'branding',
            title: <T _str="Branding" />,
            description: (
              <T _str="Add your organization’s branding to personalize the Kudos experience." />
            ),
            actionUrl: AppPaths.tenant.admin.customize(),
            visible: hasAppPermission =>
              hasAppPermission('identityApp', AppPermission.IdentityAppBrowse),
          },
        ],
      },
      {
        key: 'define-values-qualities',
        title: <T _str="Define values and qualities" />,
        isCompleted: false,
        subItems: [
          {
            key: 'values-qualities',
            title: <T _str="Values and qualities" />,
            description: (
              <T _str="Add company values and qualities to shape your culture and guide meaningful recognition." />
            ),
            actionUrl: AppPaths.tenant.admin.values(),
            visible: hasAppPermission =>
              hasAppPermission('valueApp', AppPermission.ValueAppCreateValue),
          },
        ],
      },
      {
        key: 'set-up-point-allocations',
        title: <T _str="Set up point allocations" />,
        description: (
          <T _str="Control how points are used across your organization. A clear allocation strategy helps manage spend while keeping engagement high." />
        ),
        isCompleted: false,
        helpText: (
          <T _str="Your point strategy should reflect your organization's budget and priorities. You don't need to use every allocation type. Some teams focus solely on peer-to-peer recognition, while others prioritize celebrations or awards." />
        ),
        subItems: [
          {
            key: 'peer-to-peer-points',
            title: <T _str="Peer-to-peer points" />,
            description: (
              <T _str="These are points allocated to users specifically for sending recognition to their peers. These points are for giving only." />
            ),
            actionUrl: AppPaths.tenant.admin.pointsRules('peer'),
            visible: hasAppPermission =>
              hasAppPermission('pointsApp', AppPermission.PointsAppCreateBudgetItem),
          },
          {
            key: 'celebration-points',
            title: <T _str="Celebration points" />,
            description: (
              <T _str="These are points automatically awarded to users to celebrate special occasions like birthdays and work anniversaries." />
            ),
            actionUrl: AppPaths.tenant.admin.pointsRules('celebration'),
            visible: hasAppPermission =>
              hasAppPermission('pointsApp', AppPermission.PointsAppCreateBudgetItem),
          },
          {
            key: 'point-banks',
            title: <T _str="Point banks" />,
            description: (
              <T _str="A set pool of points used to fund awards and incentives. They help you effectively plan your budget when setting up engagement initiatives." />
            ),
            actionUrl: AppPaths.tenant.admin.pointsRules('bank'),
            visible: hasAppPermission =>
              hasAppPermission('pointsApp', AppPermission.PointsAppCreateBudgetItem),
          },
        ],
      },
      {
        key: 'configure-points-preferences',
        title: <T _str="Configure points preferences" />,
        isCompleted: false,
        subItems: [
          {
            key: 'points-preferences',
            title: <T _str="Points preferences" />,
            description: (
              <T _str="Control how points work across your organization. You can disable points entirely, set the allocation period for peer-to-peer giving, choose when redeemable points expire, and manage which users are eligible to receive points." />
            ),
            actionUrl: AppPaths.tenant.admin.points('preferences'),
            visible: hasAppPermission =>
              hasAppPermission('pointsApp', AppPermission.PointsAppBrowse),
          },
        ],
      },
      {
        key: 'set-up-rewards-stores',
        title: <T _str="Set up rewards stores" />,
        isCompleted: false,
        subItems: [
          {
            key: 'rewards-stores',
            title: <T _str="Rewards stores" />,
            description: <T _str="Set up a rewards catalogue for users to redeem points." />,
            actionUrl: AppPaths.tenant.admin.rewards('setup'),
            visible: hasAppPermission =>
              hasAppPermission('rewardsApp', AppPermission.RewardsAppAdmin),
          },
        ],
      },
      {
        key: 'set-up-engagement-initiatives',
        title: <T _str="Set up engagement initiatives" />,
        isCompleted: false,
        subItems: [
          {
            key: 'awards',
            title: <T _str="Awards" />,
            description: (
              <T _str="Awards are special recognition moments for big achievements, milestones, or company events. They're more formal and higher-impact than everyday recognition, and they help boost performance, morale, and your overall recognition culture." />
            ),
            actionUrl: AppPaths.tenant.admin.awards('awards'),
            visible: hasAppPermission => hasAppPermission('awardApp', AppPermission.AwardAppBrowse),
          },
          {
            key: 'incentives',
            title: <T _str="Incentives" />,
            description: (
              <T _str="Incentives are custom challenges that motivate employees to take positive actions like completing training, practicing wellness habits, or supporting your culture." />
            ),
            actionUrl: AppPaths.tenant.admin.manageIncentives(),
            visible: hasAppPermission =>
              hasAppPermission('incentiveApp', AppPermission.IncentiveAppReview),
          },
        ],
      },
      {
        key: 'configure-celebration-preferences',
        title: <T _str="Configure celebration preferences" />,
        description: (
          <T _str="Celebrations are a fun way to highlight milestones across your organization, making it easy for employees to join in and celebrate their peers." />
        ),
        isCompleted: false,
        subItems: [
          {
            key: 'celebration-privacy',
            title: <T _str="Celebration privacy" />,
            description: (
              <T _str="Choose which milestones you'd like to celebrate within the platform." />
            ),
            actionUrl: AppPaths.tenant.admin.configureCelebrations('privacy'),
            visible: hasAppPermission =>
              hasAppPermission('celebrationApp', AppPermission.CelebrationAppEditTenantPreferences),
          },
          {
            key: 'celebration-cards',
            title: <T _str="Cards" />,
            description: (
              <T _str="Personalize the cover design and default messages that appear in cards." />
            ),
            actionUrl: AppPaths.tenant.admin.configureCelebrations('cards'),
            visible: hasAppPermission =>
              hasAppPermission('celebrationApp', AppPermission.CelebrationAppEditTenantPreferences),
          },
        ],
      },
    ],
  },
  {
    key: LaunchSectionsEnum.LAUNCH,
    title: <T _str="Launch" />,
    items: [
      {
        key: 'invite-users-to-kudos',
        title: <T _str="Invite users to Kudos" />,
        isCompleted: false,
        subItems: [
          {
            key: 'send-invite-email',
            title: <T _str="Send invite email" />,
            description: (
              <T _str="Send an invite email to officially launch Kudos and welcome your users to the platform." />
            ),
            actionUrl: AppPaths.tenant.admin.members('users'),
            visible: hasAppPermission =>
              hasAppPermission('identityApp', AppPermission.IdentityAppBrowse),
          },
        ],
      },
      {
        key: 'create-launch-announcement',
        title: <T _str="Create a launch announcement" />,
        isCompleted: false,
        subItems: [
          {
            key: 'announcements',
            title: <T _str="Announcements" />,
            description: (
              <T _str="Create a launch announcement to introduce Kudos, share what to expect, and get your team excited to join." />
            ),
            actionUrl: AppPaths.tenant.admin.announcements(),
            visible: hasAppPermission =>
              hasAppPermission('announcementApp', AppPermission.AnnouncementAppManage),
          },
        ],
      },
    ],
  },
]
